import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

export const AdminNavBuilder = () => {
  const [links, setLinks] = useState([]);
  const [form, setForm] = useState({ label: "", url: "", order: 0, opens_new_tab: false });
  const [editingId, setEditingId] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/api/admin/navigation`); setLinks(data); }
    catch (err) { if (err?.response?.status !== 404) console.error("Load nav:", err); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const addLink = async () => {
    if (!form.label || !form.url) return toast.error("Label and URL required");
    try {
      await axios.post(`${API}/api/admin/navigation`, { ...form, order: links.length });
      setForm({ label: "", url: "", order: 0, opens_new_tab: false });
      load(); toast.success("Link added!");
    } catch { toast.error("Failed to add link"); }
  };

  const deleteLink = async (id) => {
    if (!window.confirm("Remove this link?")) return;
    try { await axios.delete(`${API}/api/admin/navigation/${id}`); load(); toast.success("Deleted"); } catch { toast.error("Failed"); }
  };

  const defaultLinks = [
    { label: "Home", url: "/" },
    { label: "About", url: "/about" },
    { label: "Services", url: "/services" },
    { label: "Packages & FAQ", url: "/book" },
    { label: "Blog", url: "/blog" },
    { label: "Gallery", url: "/gallery" },
    { label: "Contact", url: "/contact" },
  ];

  return (
    <div>
      {/* Default Navigation */}
      <div style={{ background: "#fff", borderRadius: 12, padding: "1.5rem", boxShadow: "0 1px 3px rgba(0,0,0,.06)", marginBottom: "1.5rem" }}>
        <h3 style={{ fontSize: ".95rem", fontWeight: 700, color: "#1F2937", margin: "0 0 .4rem" }}>Default Navigation</h3>
        <p style={{ fontSize: ".8rem", color: "#9CA3AF", margin: "0 0 1rem" }}>These links are built into the site header and always visible.</p>
        <div style={{ display: "flex", flexWrap: "wrap", gap: ".4rem" }}>
          {defaultLinks.map(l => (
            <span key={l.url} style={{ padding: ".35rem .75rem", borderRadius: 6, background: "#F3F4F6", fontSize: ".82rem", color: "#374151", fontWeight: 500 }}>
              {l.label} <span style={{ color: "#9CA3AF", fontSize: ".7rem" }}>{l.url}</span>
            </span>
          ))}
        </div>
      </div>

      {/* Add Custom Link */}
      <div style={{ background: "#fff", borderRadius: 12, padding: "1.5rem", boxShadow: "0 1px 3px rgba(0,0,0,.06)", marginBottom: "1.5rem" }}>
        <h3 style={{ fontSize: ".95rem", fontWeight: 700, color: "#1F2937", margin: "0 0 1rem" }}>Add Custom Navigation Link</h3>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: ".75rem", alignItems: "end" }}>
          <div>
            <label style={{ display: "block", fontSize: ".8rem", fontWeight: 600, color: "#374151", marginBottom: ".3rem" }}>Label</label>
            <input value={form.label} onChange={e => setForm(p => ({ ...p, label: e.target.value }))} placeholder="Link text" data-testid="nav-label-input"
              style={{ width: "100%", padding: ".5rem .75rem", border: "1px solid #D1D5DB", borderRadius: 6, fontSize: ".85rem" }} />
          </div>
          <div>
            <label style={{ display: "block", fontSize: ".8rem", fontWeight: 600, color: "#374151", marginBottom: ".3rem" }}>URL</label>
            <input value={form.url} onChange={e => setForm(p => ({ ...p, url: e.target.value }))} placeholder="/page or https://..." data-testid="nav-url-input"
              style={{ width: "100%", padding: ".5rem .75rem", border: "1px solid #D1D5DB", borderRadius: 6, fontSize: ".85rem" }} />
          </div>
          <button onClick={addLink} data-testid="nav-add-button" style={{ padding: ".5rem 1.25rem", borderRadius: 8, border: "none", background: "linear-gradient(135deg, #EC4899, #F97316)", color: "#fff", cursor: "pointer", fontSize: ".82rem", fontWeight: 600, height: 38 }}>
            <i className="fas fa-plus" /> Add
          </button>
        </div>
        <label style={{ display: "flex", alignItems: "center", gap: ".5rem", marginTop: ".75rem", cursor: "pointer" }}>
          <input type="checkbox" checked={form.opens_new_tab} onChange={e => setForm(p => ({ ...p, opens_new_tab: e.target.checked }))} style={{ accentColor: "#EC4899" }} />
          <span style={{ fontSize: ".82rem", color: "#6B7280" }}>Open in new tab</span>
        </label>
      </div>

      {/* Custom Links List */}
      <div style={{ background: "#fff", borderRadius: 12, padding: "1.5rem", boxShadow: "0 1px 3px rgba(0,0,0,.06)" }}>
        <h3 style={{ fontSize: ".95rem", fontWeight: 700, color: "#1F2937", margin: "0 0 1rem" }}>Custom Links ({links.length})</h3>
        {links.length === 0 ? (
          <p style={{ color: "#9CA3AF", fontSize: ".85rem", textAlign: "center", padding: "2rem 0" }}>No custom links added. The default navigation is used.</p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: ".5rem" }}>
            {links.map((l, idx) => (
              <div key={l.id} style={{ display: "flex", alignItems: "center", gap: ".75rem", padding: ".7rem .9rem", background: "#F9FAFB", borderRadius: 8, border: "1px solid #E5E7EB" }}>
                <i className="fas fa-grip-vertical" style={{ color: "#D1D5DB", cursor: "grab" }} />
                <div style={{ flex: 1 }}>
                  <span style={{ fontWeight: 600, color: "#1F2937", fontSize: ".85rem" }}>{l.label}</span>
                  <span style={{ color: "#EC4899", fontSize: ".78rem", marginLeft: ".5rem" }}>{l.url}</span>
                  {l.opens_new_tab && <span style={{ marginLeft: ".5rem", fontSize: ".65rem", color: "#9CA3AF" }}><i className="fas fa-external-link-alt" /></span>}
                </div>
                <button onClick={() => deleteLink(l.id)} style={{ width: 30, height: 30, borderRadius: 6, border: "1px solid #FEE2E2", background: "#FEF2F2", cursor: "pointer", color: "#EF4444", fontSize: ".75rem" }}>
                  <i className="fas fa-trash" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
